import { DealBase } from '@baseClass/DealBase';
import { VehicleBase } from '@baseClass/VehicleBase';
import { DateString } from '@core/_globals/types/date-settings';
import Application from '@core/classes/Applications/Application';
import ApplicationsBusinessInfo from '@core/classes/Applications/ApplicationsBusinessInfo';
import ApplicationsPreFilledData from '@core/classes/Applications/ApplicationsPreFilledData';
import CustomerRecord from '@core/classes/Customer/CustomerRecord';
import CustomerResidence from '@core/classes/Customer/CustomerResidence';
import DealEmployee from '@core/classes/DealEmployee';
import FIMenuCustomer from '@core/classes/FIMenuCustomer';
import { PlaidCustomerIncomeData } from '@core/classes/Plaid/PlaidCustomerIncomeData';
import { CustomerRequest } from '@core/classes/Requests/Customer/CustomerRequest';
import { MobileVerifyRequest } from '@core/classes/Requests/MobileVerifyRequest';
import SaturnResponse from '@core/classes/Responses/SaturnResponse';
import { CustomerType } from '@core/classes/SharedEnums';
import { StoreClass } from '@core/classes/Store/StoreClass';
import CustomerApplicationHelper, {
	checkIfWeNeedANewApplication,
	createCustomerApplicationFromCustomer,
	hasCustomerApplication,
	isCreditApplicationComplete,
} from '@core/helpers/customer-application-helper';
import modalApplicantInfo from '@core/modals/modalApplicantInfo.vue';
import modalConfirmCustomerInfo from '@core/modals/modalConfirmCustomerInfo.vue';
import modalFullCustomerApplication from '@core/modals/modalFullCustomerApplication.vue';
import ModalInfo from '@core/modals/modalInfo.vue';
import modalRetrieveCustomer from '@core/modals/modalRetrieveCustomer.vue';
import modalSendLinkToCustomer from '@core/modals/modalSendLinkToCustomer.vue';
import api from '@core/services/api/_api';
import { ICustomerPortalCombinedData, IEditApplicationRequest } from '@core/services/api/api.customerPortal';
import { dywAPI } from '@core/services/api/api.dataYourWay';
import $modal from '@core/services/modal';
import util, { handleSaturnResponseErrorMessages } from '@core/services/util';
import { isDateTheSame } from '@core/services/util.date';
import { useBusinessStore } from '@core/stores/customer/BusinessStore';
import { useUserStore } from '@core/stores/customer/UserStore';
import LogRocket from 'logrocket';
import { nextTick } from 'vue';
import { Router } from 'vue-router';

export interface IEmailRequest {
	sendToList: string[];
	isBodyHtml: boolean;
	emailSubject?: string;
	emailBody?: string;
}

export interface IPhoneRequest {
	toPhoneNumber: string;
	body: string;
	storeCode: string;
}

export interface IStore {
	additions: any[];
	cdkCode: string;
	cdkSettings: any;
	countyTaxRate: number;
	customerApplicationSettings: any;
	defaultFees: any[];
	emailAddressesForHouseDeals: string;
	emailAddressesForHouseUnwind: string;
	emailAddressesForReports: string;
	gwcCode: string;
	id: string;
	isLeasePaymentTaxed: boolean;
	meta: any;
	nsdCode: string;
	partnerCodes: any[];
	preloads: any[];
	products: any[];
	stateTaxRate: number;
	storeAccountingInfo: any;
	storeAddress: string;
	storeAddressExtra: string;
	storeCity: string;
	storeCode: string;
	storeCounty: string;
	storeDateTime: DateString;
	storeFiles: any[];
	storeName: string;
	storePhone: string;
	storeSettings: any;
	storeState: string;
	storeTimeZone: string;
	storeUser: string;
	storeWebsiteUrl: string;
	storeZip: string;
	usWarrantyCompanyCode: string;
	usWarrantyStoreCode: string;
}

/*
    @@@@@@@@@@ --->  READ ME  <--- @@@@@@@@@@

    Let's keep the methods clean and focused. Implement specific solutions directly within the
    main code rather than using helpers. However, if you think an implementation will be reused in different
    places, feel free to add more parameters to the current methods or create new methods.
*/

/**
 * @param actionOnRetrieveCustomer - Function to be called when a customer is retrieved
 * @param actionOnRetrieveCompany - Function to be called when a company is retrieved
 * @param canRetrieveCompany - Whether the company can be retrieved
 * @param sortCustomersToTop - Array of customer IDs to sort to the top of the list
 */
export interface IModalRetrieveCustomerProps {
	actionOnRetrieveCustomer: (customer: any) => void;
	actionOnRetrieveCompany: (customer: any) => void;
	canRetrieveCompany: boolean;
	sortCustomersToTop?: string[];
	removeIDVRestrictions?: boolean;
	searchRestrictions?: CustomerRequest;
}

/**
 * Opens a modal to retrieve customers
 * @param props - Object containing the properties for the modal
 * @param toggleCreateLead - Whether to toggle the create lead button
 */
export async function openRetrieveCustomersModal(props: IModalRetrieveCustomerProps, toggleCreateLead = false) {
	const modalParams = {
		name: 'modalRetrieveCustomer',
		passedData: {
			modalView: true,
			actionOnRetrieveCustomer: props.actionOnRetrieveCustomer,
			actionOnRetrieveCompany: props.actionOnRetrieveCompany,
			canRetrieveCompany: props.canRetrieveCompany,
			canCreateLeads: toggleCreateLead,
			openModalApplicantInfo: openModalApplicantInfo,
			sortCustomersToTop: props.sortCustomersToTop,
			removeIDVRestrictions: props.removeIDVRestrictions,
			searchRestrictions: props.searchRestrictions,
		},
		backdrop: true,
	};
	$modal.open(modalRetrieveCustomer, modalParams);
}

/**
 * Upserts the customer to CDK if the last modified date has change or the customer number is missing.
 *
 * @export
 * @param {CustomerRecord} customer
 * @param {string} storeCode
 * @return {*}
 */
export async function syncCustomerExternally(customer: CustomerRecord, storeCode: string): Promise<void> {
	try {
		const { lastModifiedAt, cdkCustomerInfo } = customer;

		if (!isDateTheSame(cdkCustomerInfo.lastExternalDMSSyncDate, lastModifiedAt)) {
			const response = (await dywAPI.upsertCustomer({ customerId: customer.id, identifications: customer.personalInfo?.identifications, storeCode })).data;

			handleSaturnResponseErrorMessages(response);

			if (response.payload) customer.cdkCustomerInfo = response.payload;
		}
	} catch (err: any) {
		console.error(err);
	}
	return null;
}

export async function getUnHashedSSN(customer: CustomerRecord) {
	const ssnToUnhash = customer.personalInfo?.ssnHashed;
	try {
		if (ssnToUnhash) {
			const response = await api.utilities.decrypt(ssnToUnhash);
			return `${response.data}`.slice(0, 3) + '-' + `${response.data}`.slice(3, 5) + '-' + `${response.data}`.slice(5, 9);
		}
	} catch (err: any) {
		console.error(err);
	}
	return null;
}

/**
 * Opens a modal to display applicantInfoPanel
 * @param store
 * @param customer
 * @returns
 */
export async function openModalApplicantInfo(store: any, customer = new FIMenuCustomer()) {
	const modalParams = {
		name: 'modalApplicantInfo',
		passedData: {
			title: 'Create Customer Application',
			prefilledData: new ApplicationsPreFilledData(),
			acceptText: 'Create',
			cancelText: 'Close',
			isCustomer: true,
			customer: customer,
			store: store,
		},
		backdrop: false,
	};

	return $modal.open(modalApplicantInfo, modalParams);
}

/**
 * This method creates a customer application from an FIMenu Customer. Ideally should be customer
 *
 * @param storeCode
 * @param customer FIMenuCustomer
 * @param lng Either "English" or "Spanish"
 */
export async function openModalCreateCustomerApplicationFromCustomer(storeCode: string, customer: FIMenuCustomer, lng = 'English') {
	const modalParams = {
		name: 'modalConfirmCustomerInfo',
		passedData: {
			title: 'Create Customer Application',
			info: 'Please confirm the e-mail and cellular phone number are accurate.  We will be sending an e-mail and SMS code to create the quote application.',
			id: customer.id,
			customer: customer,
			isCustomer: true,
			acceptText: 'Create',
			cancelText: 'Cancel',
			language: lng,
			storeCode: storeCode,
		},
		backdrop: false,
	};
	$modal.open(modalConfirmCustomerInfo, modalParams);
}

/**
 *  Display a full customer application
 * @param customer FIMenuCustomer
 */
export async function openModalDisplayFullCustomerApplication(customer: FIMenuCustomer) {
	$modal.open(modalFullCustomerApplication, {
		name: 'modalFullCustomerApplication',
		passedData: {
			customer: customer,
		},
		backdrop: true,
	});
}

export async function openModalSendLinkToCustomer(
	customer: FIMenuCustomer,
	storeCode: string,
	title: string,
	questionKey: string,
	isSingleQuestionEdit = null as boolean,
	resetDateCompleted = false,
	customerType: CustomerType = CustomerType.Customer,
) {
	$modal.open(modalSendLinkToCustomer, {
		name: 'modalSendLinkToCustomer',
		passedData: {
			title: `${title} Section`,
			generateLink: () => generateApplicationEditLink(customer, storeCode, questionKey, isSingleQuestionEdit, resetDateCompleted, true),
			handleSMSIssue: async () => await handleSMSIssue(customer.id, storeCode, customerType),
		},
		backdrop: true,
		postFunction: (sendLinkMethod: string) => sendLinkToCustomer(customer, storeCode, sendLinkMethod, questionKey, isSingleQuestionEdit, resetDateCompleted),
	});
}

export async function generateApplicationEditLink(
	customer: FIMenuCustomer,
	storeCode: string,
	questionKey: string,
	isSingleQuestionEdit = null as boolean,
	resetDateCompleted = false,
	isInStore = false,
) {
	const response = await api.applications.generateApplicationEditLink({
		customerId: customer.id,
		questionKey,
		isSingleQuestionEdit,
		resetDateCompleted,
		storeCode: storeCode,
		isInStore: isInStore,
	});

	return response.data;
}

export async function sendLinkToCustomer(
	customer: FIMenuCustomer,
	storeCode: string,
	sendLinkMethod: string,
	questionKey: string,
	isSingleQuestionEdit = null as boolean,
	resetDateCompleted = false,
) {
	const link = await generateApplicationEditLink(customer, storeCode, questionKey, isSingleQuestionEdit, resetDateCompleted);

	const newLinkRequest = {
		originalLink: link,
		domain: 'https://' + window.location.host,
		store: storeCode,
		phoneNumber: customer.personalInfo?.cell,
	};

	let newLink = link;
	await api.applications.getRedirectLink(newLinkRequest).then((response: any) => {
		if (response.data) {
			newLink = response.data;
		}
	});

	if (sendLinkMethod == 'email') {
		const request: any = {
			sendToList: [customer.personalInfo?.email],
			isBodyHtml: true,
		};

		if (customer.personalInfo?.language == 'Spanish') {
			request.emailSubject = 'Actualización de Aplicación';
			request.emailBody = `<p>Por favor siga el enlace para actualizar su aplicación: </p><a href=${newLink}>Edit Application</a>`;
		} else {
			request.emailSubject = 'Application Update';
			request.emailBody = `<p>Please follow the link to update your application: </p><a href=${newLink}>Edit Application</a>`;
		}

		api.applications
			.sendEditLink({ customerId: customer.id, emailRequest: request })
			.then((response: any) => {
				if (response.data) {
					util.toastr('success', 'Success', 'Successfully sent email');
				}
			})
			.catch((error: any) => {
				// eslint-disable-next-line no-console
				console.error('Error', error);
				util.toastr('error', 'Error', 'Failed to send text email');
			});
	} else if (sendLinkMethod == 'text') {
		const request = {
			toPhoneNumber: '+1' + customer.personalInfo.cell,
			body:
				customer.personalInfo?.language == 'Spanish'
					? 'Por favor siga el enlace para actualizar su aplicación: ${newLink}'
					: 'Please follow the link to update your application: ' + newLink,
			storeCode: storeCode,
		};

		api.applications
			.sendEditLink({
				customerId: customer.id,
				messageRequest: request,
			})
			.then((response: any) => {
				if (response.data) {
					util.toastr('success', 'Success', 'Successfully sent message');
				}
			})
			.catch((error: any) => {
				// eslint-disable-next-line no-console
				console.error('Error', error);
				util.toastr('error', 'Error', 'Failed to send message');
			});
	}
}

//#region ----------Direct Customer Stuff -------  //

export async function sendPortalLink(request: IEditApplicationRequest) {
	try {
		let portalLinkResponse = await api.customerPortal.sendPortalLink(request);
		if (portalLinkResponse.status == 200 && portalLinkResponse.data) {
			util.toastr('success', 'Success', 'Successfully sent.');
			return portalLinkResponse.data;
		}
	} catch (error) {
		util.toastr('error', 'Error', 'Failed to send.');
	}
}

export async function generateTinyUrl(link: string, storeCode: string, phoneNumber: string) {
	const newLinkRequest = {
		originalLink: link,
		domain: 'https://' + window.location.host,
		storeCode: storeCode,
		phoneNumber: phoneNumber,
	};

	try {
		const shortRedirectLink = await api.applications.getRedirectLink(newLinkRequest);
		if (shortRedirectLink.status == 200 && shortRedirectLink.data) {
			return shortRedirectLink.data;
		}
	} catch (error) {
		throw new Error('Error generating short link.');
	}
}

export const getCustomerById = async (id: string) => {
	try {
		const customerResponse = await api.customers.getCustomerById(id);
		if (customerResponse?.data) {
			return customerResponse.data;
		} else return null;
	} catch (error) {
		console.error(error);
	}
};

export const getCustomerAndGenerateLink = async (encryptedId: string): Promise<string | null> => {
	try {
		// decryptId
		if (!encryptedId) return null;
		const decryptedId = util.decryptJSONText(encryptedId);
		const customerId = JSON.parse(decryptedId);

		//Get customer by id
		const { customer } = await getInformationForCustomerPortal(customerId);

		const link = await generateLinkForQRCode(customer, null, customer.storeCodes[0], false);
		return link;
	} catch (error) {
		util.toastr('error', 'Error', 'Unable to retrieve information. Please contact support.');
		console.error(error);
	}
};

/**
 * Gets the latest customer application for a customer. Uses try/catch to handle errors.
 * @param customerId
 * @returns {Application}
 */
export const getLatestCustomerApplication = async (customerId: string) => {
	try {
		const response = await api.applications.getByCustomer(customerId);
		if (response.data) {
			return new Application(response.data);
		}
		return null;
	} catch (error) {
		console.error(error);
		util.toastr('error', 'Error', 'Error retrieving customer application. Please contact support.');
	}
};

export const generateLinkForQRCode = async (customer: any, store: StoreClass, storeCode: string = null, shouldCheckIfCustomerApp = true) => {
	const currentStoreCode = store?.storeCode ?? storeCode ?? null;
	if (currentStoreCode == null) return null;

	// Get the latest customer application
	let currentCustomerApp = await getLatestCustomerApplication(customer.id);

	// currentCustomerApp being null can be the case for customers who were created before CustomerApp launched
	if (shouldCheckIfCustomerApp || currentCustomerApp == null) {
		const newCustomerApp = await checkIfWeNeedANewApplication(currentCustomerApp, customer, null, currentStoreCode);
		// Only update `currentCustomerApp` if `newCustomerApp` is not null or undefined (it entered the if stmt)
		if (newCustomerApp != null) {
			currentCustomerApp = newCustomerApp;
		}
	}

	const phone = customer.personalInfo?.cell;
	const phoneLastFour = phone.toString().slice(-4);
	const link: string = `/customer-portal/phoneVerification?emailCode=${currentCustomerApp?.emailCode}&storeCode=${currentStoreCode}&lastFour=${phoneLastFour}`;

	const shortLink = await generateTinyUrl(link, currentStoreCode, phone);
	return shortLink;
};

export async function sendLinkToCustomerForPortal(customer: CustomerRecord, language: string, store: StoreClass, sendLinkMethod: string) {
	const currentCustomerApp = await getLatestCustomerApplication(customer.id);

	if (!currentCustomerApp || CustomerApplicationHelper.isCustomerApplicationExpiredByGlobalSettingsCheck(currentCustomerApp.dateCompleted)) {
		try {
			const customerApp = await createCustomerApplicationFromCustomer(currentCustomerApp, customer, false, store.storeCode);
			if (customerApp != null) return util.toastr('success', 'Success', 'Successfully sent.');
		} catch (error) {
			util.toastr('error', 'Error', 'Error sending link to customer. Try again.');
		}
	}

	const spanishMessage: string = 'Por favor siga el enlace para entrar en su portal';
	const spanishButtonText: string = 'Entra al portal';
	const englishMessage: string = 'Please follow the link to enter your portal';
	const englishButtonText: string = 'Enter portal';
	const phone: string = customer.personalInfo?.cell;
	const phoneLastFour: string = phone.toString().slice(-4);
	const link: string = `/customer-portal/phoneVerification?emailCode=${currentCustomerApp?.emailCode}&storeCode=${store.storeCode}&lastFour=${phoneLastFour}`;
	// let link = await redirectToCustomerPortal(customer.id);
	const shortLink = await generateTinyUrl(link, store.storeCode, phone);
	if (sendLinkMethod == 'email') {
		const emailRequest: IEmailRequest = {
			sendToList: [customer.personalInfo.email],
			isBodyHtml: true,
			emailSubject: null,
			emailBody: null,
		};

		if (language == 'Spanish') {
			emailRequest.emailSubject = 'Entra al Portal';
			emailRequest.emailBody = `<p>${spanishMessage}: </p><a href=${shortLink}>${spanishButtonText}</a>`;
		} else {
			emailRequest.emailSubject = 'Enter Portal';
			emailRequest.emailBody = `<p>${englishMessage}: </p><a href=${shortLink}>${englishButtonText}</a>`;
		}

		sendPortalLink({ customerId: customer.id, emailRequest: emailRequest });
	} else if (sendLinkMethod == 'text') {
		const phoneRequest: IPhoneRequest = {
			toPhoneNumber: '+1' + customer.personalInfo.cell,
			body: language == 'Spanish' ? `${spanishMessage}: ${shortLink}` : `${englishMessage}: ${shortLink}`,
			storeCode: store.storeCode,
		};

		sendPortalLink({ customerId: customer.id, messageRequest: phoneRequest });
	}
}

/** For manually verifying the OTP */
export async function handleSMSIssue(customerId: string, storeCode: string, customerType = CustomerType.Customer) {
	nextTick(() => {
		$modal.open(ModalInfo, {
			name: 'modalInfo',
			passedData: {
				title: 'Notifying General Managers',
				info: `This action will notify all GENERAL MANAGERS located at store: ${storeCode}.\n\nThey will provide you with the One Time Password, on the Customer's behalf.`,
				additionalInfo: 'Please ensure that the customer is who they claim they are before giving them the OTP.',
				acceptText: 'I Accept',
				cancelText: 'Cancel',
			},
			backdrop: true,
			postFunction: async () => {
				try {
					const mobileVerifyRequest = new MobileVerifyRequest({
						customerId,
						storeCode,
						customerType,
					});
					const response = (await api.applications.manuallyVerifyOTP(mobileVerifyRequest))?.data;
					if (response && response.messages.length < 1) {
						util.toastr('success', 'One Time Password', 'General Manager(s) Were Notified!');
					} else {
						util.toastr('error', 'One Time Password', response?.messages[0] ?? 'Error notifying General Manager(s)', 5000);
					}
				} catch (error) {
					util.toastr('error', 'One Time Password', error);
					console.error(error);
				}
			},
		});
	});
}

/**
 * @param customerId
 * @returns This is a direct link to the portal
 */
export async function redirectToCustomerPortal(customerId: string) {
	try {
		const response = await api.customerPortal.redirectToPortal(customerId);

		if (response && response.status == 200 && response.data) {
			return response.data;
		}
	} catch (error) {
		throw new Error('Unable to redirect to customer portal link', { cause: error });
	}
}

export async function getInformationForCustomerPortal(customerId: string): Promise<ICustomerPortalCombinedData> {
	try {
		const response = await api.customerPortal.getCustomerInformation(customerId);
		const saturnResponse = new SaturnResponse<ICustomerPortalCombinedData>(response.data);
		saturnResponse.throwAnyError();

		const responsePayload = saturnResponse.payload;

		return {
			customer: new CustomerRecord(responsePayload.customer),
			customerApplication: new Application(responsePayload.customerApplication),
			businessCustomer: responsePayload.businessCustomer,
			dealPayments: responsePayload.dealPayments,
			customerVehicles: responsePayload.customerVehicles,
			dealsByVehicle: responsePayload.dealsByVehicle,
			plaidIncomeData: new PlaidCustomerIncomeData(responsePayload.plaidIncomeData),
		};
	} catch (error) {
		console.error('Error retrieving customer information: \n', error);
		return null;
	}
}

/** @param {CustomerRecord} businessCustomer  @returns a Customer Record for Business Customer. */
export async function createBusinessCustomer(
	businessCustomer: ApplicationsBusinessInfo,
	primaryCustomerId: string | string[],
	storeCodes: string[],
	currentAddress: CustomerResidence,
	associatedSalesPersons: DealEmployee[],
) {
	try {
		const createBusinessCustomerRequest = {
			businessCustomer: businessCustomer,
			primaryCustomerId: primaryCustomerId,
			storeCodes: storeCodes,
			currentAddress: currentAddress,
			associatedSalesPersons: associatedSalesPersons,
		};

		const businessResponse = await api.customerPortal.createBusinessCustomerFromPortal(createBusinessCustomerRequest);
		if (businessResponse.status == 200 && businessResponse.data.payload !== null) {
			util.toastr('success', 'Success', 'Successfully added business.');
			const businessStore = useBusinessStore();
			const userStore = useUserStore();

			businessStore.setBusiness(businessResponse.data.payload);
			userStore.setBusinessData(businessResponse.data.payload);
			return businessStore;
		}
		if (businessResponse.status !== 200) {
			util.toastr('error', 'Error', 'Error creating business. Try again.');
			return;
		}
	} catch (error) {
		LogRocket.log('Error creating business customer', error);
		util.toastr('error', 'Error', 'Error adding. Please reach out to support.');
	}
}

export async function openModalSendLinkToCustomerForPortal(customer: CustomerRecord, store: StoreClass) {
	try {
		$modal.open(modalSendLinkToCustomer, {
			name: 'modalSendLinkToCustomer',
			passedData: {
				title: `Send Customer To Portal`,
				generateLink: () => generateLinkForQRCode(customer, store),
				handleSMSIssue: async () => await handleSMSIssue(customer.id, store.storeCode),
			},
			backdrop: true,
			postFunction: (sendLinkMethod: string) => sendLinkToCustomerForPortal(customer, customer.personalInfo.language ?? 'English', store, sendLinkMethod),
		});
	} catch (error) {
		console.error(error);
	}
}

/**
 * Maps ApplicationsBusinessInfo to CustomerRecord
 * @param {ApplicationsBusinessInfo} businessInfo
 * @returns {CustomerRecord}
 */
function mapToCustomerRecord(businessInfo: ApplicationsBusinessInfo): CustomerRecord {
	const customerRecord = new CustomerRecord();
	customerRecord.businessInfo = businessInfo;
	return customerRecord;
}

export async function encryptSensitiveData(value: string) {
	try {
		const response = await api.utilities.encrypt(value);
		if (response) {
			const last4: string = value.slice(-4);
			return {
				hashed: response.data.encryptedValue,
				last4: last4,
			};
		}
	} catch (error) {
		console.error(error);
		util.toastr('error', 'Error', 'Error saving. Please re-enter it and try again.');
	}
}

export const sendCodeToNewEmail = async (req: any) => {
	try {
		const response = await api.customerPortal.changeEmailRequest(req);
		if (response) {
			util.toastr('success', 'Success', 'Successfully sent. Please check your email.');
			return response.data.payload;
		}
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error sending code. Please try again.');
	}
};

export const sendCodeToNewPhone = async (req: any) => {
	try {
		const response = await api.customerPortal.changePhoneRequest(req);
		if (response) {
			util.toastr('success', 'Success', 'Successfully sent. Please check your phone.');
			return response.data.payload;
		}
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error sending code. Please try again.');
	}
};

export const verifyCodeToNewEmail = async (req: any) => {
	try {
		const response = await api.customerPortal.verifyNewEmailRequest(req);
		if (response?.data?.payload) {
			util.toastr('success', 'Success', 'Successfully verified!');
			return response.data.payload;
		} else return util.toastr('error', 'Error', 'Error validating code. Please try again.');
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error validating code. Please try again.');
		return null;
	}
};

export const verifyCodeToNewPhoneNumber = async (req: any) => {
	try {
		const response = await api.customerPortal.verifyNewPhoneRequest(req);
		if (response?.data.payload) {
			util.toastr('success', 'Success', 'Successfully verified!');
			return response.data.payload;
		} else return util.toastr('error', 'Error', 'Error validating code. Please try again.');
	} catch (error) {
		console.error('Error sending code', error);
		util.toastr('error', 'Error', 'Error validating code. Please try again.');
		return null;
	}
};

export const deleteCustomer = async (customerId: string, shouldDeleteDeals: boolean = false) => {
	try {
		const customerResponse = await api.customers.deleteCustomer(customerId, shouldDeleteDeals);
		if (customerResponse.status == 200) {
			util.toastr('success', 'Customer Deleted', `Customer has been deleted successfully!`);
		} else {
			util.toastr('error', 'Delete Customer', 'Error deleting customer. Please try again.');
		}
	} catch (error) {
		util.toastr('error', 'Delete Customer', 'Error deleting customer. Please try again.');
		console.error(error);
	}
};

//#endregion

//#region ---------- Widget stuff ------------ //

/**
 * @param {Application} customerApp
 * @returns Either complete or incomplete, for the Credit App widget pill text
 */
export function getCreditAppPillLabel(customerApp: Application, language: string): string {
	if (!customerApp?.id) return '';
	if (hasCustomerApplication(customerApp) && isCreditApplicationComplete(customerApp)) {
		return language == 'English' ? 'Completed' : 'Completado';
	}
	return language == 'English' ? 'Incomplete' : 'Incompletado';
}

export function getCreditAppPillStatusColor(customerApp: Application) {
	if (hasCustomerApplication(customerApp) && isCreditApplicationComplete(customerApp)) {
		return 'success';
	}
	return 'error'; // Assuming 'error' is the color code for incomplete
}

export function disableCreditAppButton(customerApp: Application): boolean {
	if (!customerApp?.id) return true;
	return hasCustomerApplication(customerApp) && isCreditApplicationComplete(customerApp);
}

//#endregion

export function redirectToGarage(router: Router, id: string) {
	router.push({ name: 'garage', query: { id } });
}

export function redirectToPayments(router: Router, id: string, dealId: string) {
	router.push({ name: 'send-payment', query: { id, dealId } });
}

export function redirectToIncomeVerification(router: Router, id: string) {
	router.push({ name: 'income-verification', query: { id } });
}

export function redirectToPaperwork(router: Router, id: string, vehicleId: string) {
	router.push({ name: 'sign-paperwork', query: { id, vehicleId } });
}

export function rerouteHome(router: Router, id: string) {
	router.push({ name: 'home', query: { id } });
}

export function redirectToProfile(router: Router, id: string) {
	router.push({ name: 'profile', query: { id } });
}

export const redirectToBusinessPage = (router: Router, id: string | string[]) => {
	router.push({ name: 'business', query: { id } });
};

export const redirectToEditBusiness = (router: Router, id: string | string[]) => {
	router.push({ name: 'add-business', query: { id } });
};

export const redirectToAddVehiclePage = (router: Router, id: string | string[]) => {
	router.push({ name: 'add-vehicle', query: { id } });
};

export function getOwnerName(ownerId: any, deal: DealBase<CustomerRecord, VehicleBase, any>, index: number) {
	return deal.getCustomerById(ownerId)?.getFullName() ?? 'Owner ' + (index + 1);
}

const CustomerHelper = {
	rerouteHome,
	deleteCustomer,
	sendPortalLink,
	generateTinyUrl,
	getCustomerById,
	redirectToGarage,
	redirectToPaperwork,
	redirectToIncomeVerification,
	redirectToPayments,
	redirectToProfile,
	encryptSensitiveData,
	getCreditAppPillLabel,
	disableCreditAppButton,
	redirectToEditBusiness,
	redirectToBusinessPage,
	createBusinessCustomer,
	redirectToCustomerPortal,
	sendLinkToCustomerForPortal,
	getCustomerAndGenerateLink,
	getCreditAppPillStatusColor,
	getInformationForCustomerPortal,
};

export default CustomerHelper;
